import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from './ui/collapsible';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from './ui/table';
import { ChevronDown, Calendar, Clock, UtensilsCrossed } from 'lucide-react';

const ceremonyRunSheet = [
  { time: '9:00 AM', event: 'Set Up Ceremony', note: '' },
  { time: '1:50 PM', event: 'Guests Seated', note: '' },
  { time: '2:00 PM', event: 'Ceremony Begins', note: 'Song, then walk down the aisle' },
  { time: '', event: 'Vows & Ring Warming', note: 'Talk, ring warming, vows, the kiss' },
  { time: '', event: 'Walk Back Down', note: 'Confetti & the dip' },
  { time: '', event: 'Photos', note: 'Family photos, then just the two of us' },
  { time: '3:30 PM', event: 'Grazing Boards', note: 'Served once the reception is set up' },
  { time: '6:00 PM', event: 'Wood-Fired Pizza Dinner', note: 'Two ovens — including a gluten & dairy free option' },
  { time: '7:00 PM', event: 'Dessert', note: 'Wedding cake, milk tarts & cheesecakes' },
];

const mealsSchedule = [
  { day: 'Friday', breakfast: '—', lunch: '—', dinner: 'Takeout — your own responsibility' },
  { day: 'Saturday', breakfast: 'Bacon and eggs, muffins & hash browns', lunch: 'Brioche buns, sizzle steaks, cheese & sausages', dinner: 'Boerewors, bread rolls, tomato sauce, corn & salad' },
  { day: 'Sunday', breakfast: 'Bacon, scrambled eggs, sausages & toast', lunch: 'Salami, ham, cheese & shredded chicken wraps', dinner: 'Grazing boards & pizza van' },
  { day: 'Monday', breakfast: 'Bacon, eggs & toast', lunch: '—', dinner: '—' },
];

const WeekendDetails = () => {
  const schedule = [
    {
      date: 'September 18',
      day: 'Friday',
      title: 'Arrivals & Welcome',
      description: 'Settle into Sherwood Farms and enjoy the beautiful surroundings',
      details: [
        'Check-in from 3:00 PM',
        'Organise your own takeout from local towns',
        'Dirty Creek or Kungala (15 mins away)'
      ],
      highlight: false
    },
    {
      date: 'September 19',
      day: 'Saturday',
      title: 'Farm Day',
      description: 'Explore the farm and relax with friends and family',
      details: [
        'Farm Activities: Tennis, swimming pool, hot tub, short hikes, kayaking in the dam, visiting reindeers',
        'Breakfast: Bacon and eggs, muffins & hash browns',
        'Lunch: Brioche buns, sizzle steaks, cheese & sausages',
        'Dinner: Boerewors, bread rolls, tomato sauce, corn & salad',
        'All food served at the Pavilion',
        'Evening: Fire & Smores'
      ],
      highlight: false
    },
    {
      date: 'September 20',
      day: 'Sunday',
      title: 'Wedding Ceremony',
      description: 'Our special day!',
      details: [
        'Breakfast: Bacon, scrambled eggs, sausages & toast',
        'Lunch: Salami, ham, cheese & shredded chicken wraps',
        'Ceremony begins at 2:00 PM — see the full run sheet below',
        'Family photos, then just the two of us',
        'Grazing boards served once the reception is set up',
        'Dinner 6-8pm: Wood-fired pizza',
        'Dessert: Wedding cake, milk tarts & cheesecakes'
      ],
      highlight: true
    },
    {
      date: 'September 21',
      day: 'Monday',
      title: 'Farewell Breakfast',
      description: 'One last meal together before departures',
      details: [
        'Farewell Breakfast at 8:00 AM',
        'Share your favorite memories',
        'Check-out by 10:00 AM'
      ],
      highlight: false
    }
  ];

  return (
    <section id="details" className="py-16 sm:py-20 bg-primary/5" data-testid="schedule-section">
      <div className="section-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-serif mb-4 text-primary">Weekend Details</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Four days of celebration at Sherwood Farms
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {schedule.map((day, index) => (
            <DayCard key={day.date} day={day} index={index} />
          ))}
        </div>

        {/* Wedding Day Ceremony Run Sheet */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-5xl mx-auto mt-10"
          data-testid="ceremony-run-sheet-section"
        >
          <Card className="gold-border overflow-hidden">
            <CardHeader className="bg-primary/5">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                <CardTitle className="text-2xl font-serif text-primary">Wedding Day Timeline</CardTitle>
              </div>
              <CardDescription>Sunday, September 20 — Ceremony &amp; Reception</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <ol className="relative border-l-2 border-primary/20 ml-3 space-y-6">
                {ceremonyRunSheet.map((item, idx) => (
                  <li key={idx} className="relative pl-6" data-testid={`ceremony-timeline-item-${idx}`}>
                    <span
                      className="absolute -left-[7px] top-1.5 w-3 h-3 rounded-full"
                      style={{ backgroundImage: 'linear-gradient(135deg, #F9E27D 0%, #D4AF37 40%, #B8962E 60%, #F6E27A 100%)' }}
                    />
                    <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-3">
                      <span className="text-sm font-semibold text-primary shrink-0 w-20">{item.time}</span>
                      <div>
                        <p className="font-medium text-foreground">{item.event}</p>
                        {item.note && (
                          <p className="text-sm text-muted-foreground">{item.note}</p>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        </motion.div>

        {/* Meals & Catering */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="max-w-5xl mx-auto mt-10"
          data-testid="meals-schedule-section"
        >
          <Card className="gold-border overflow-hidden">
            <CardHeader className="bg-primary/5">
              <div className="flex items-center gap-2">
                <UtensilsCrossed className="w-5 h-5 text-primary" />
                <CardTitle className="text-2xl font-serif text-primary">Meals &amp; Catering</CardTitle>
              </div>
              <CardDescription>What's on the menu, Friday through Monday</CardDescription>
            </CardHeader>
            <CardContent className="pt-6 overflow-x-auto">
              <Table data-testid="meals-schedule-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-primary font-semibold">Day</TableHead>
                    <TableHead className="text-primary font-semibold">Breakfast</TableHead>
                    <TableHead className="text-primary font-semibold">Lunch</TableHead>
                    <TableHead className="text-primary font-semibold">Dinner</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mealsSchedule.map((row) => (
                    <TableRow key={row.day}>
                      <TableCell className="font-medium text-foreground whitespace-nowrap">{row.day}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{row.breakfast}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{row.lunch}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{row.dinner}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="text-xs text-muted-foreground mt-4 italic">
                Sunday dinner is wood-fired pizza with two ovens, including a gluten &amp; dairy free option.
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};

const DayCard = ({ day, index }) => {
  const [isOpen, setIsOpen] = useState(day.highlight);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <Card className={`transition-shadow duration-300 hover:shadow-[0_18px_44px_rgba(18,58,42,0.14)] ${
          day.highlight ? 'gold-border' : ''
        }`}>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer" data-testid="schedule-day-toggle">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4 text-primary" />
                    <span className="text-sm font-medium text-primary">{day.day}</span>
                    {day.highlight && (
                      <Badge variant="default" className="ml-2 bg-primary">
                        Ceremony Day
                      </Badge>
                    )}
                  </div>
                  <CardTitle className="text-xl sm:text-2xl font-serif mb-1">
                    {day.date}
                  </CardTitle>
                  <CardDescription className="text-base">
                    {day.title}
                  </CardDescription>
                </div>
                <ChevronDown 
                  className={`w-5 h-5 text-muted-foreground transition-transform duration-200 ${
                    isOpen ? 'rotate-180' : ''
                  }`}
                />
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          
          <CollapsibleContent>
            <CardContent className="pt-0">
              <p className="text-muted-foreground mb-3">{day.description}</p>
              <ul className="space-y-2">
                {day.details.map((detail, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm">
                    <span className="text-primary mt-1 font-bold">•</span>
                    <span>{detail}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </motion.div>
  );
};

export default WeekendDetails;
